<template>
    <v-dialog v-model="dialogOpen" :max-width="$vuetify.breakpoint.smAndDown ? undefined : 1140" :fullscreen="$vuetify.breakpoint.smAndDown" content-class="doc-dialog-container">
        <v-card v-if="doc" class="doc-dialog-card">

            <!-- ══════════════════════════════════════
                 TOOLBAR
            ══════════════════════════════════════════ -->
            <div class="doc-dialog-toolbar">
                <!-- Editable title -->
                <div v-if="!editingTitle" class="doc-dialog-title" @click="canEdit && !editing && startEditTitle()">
                    {{ doc.title }}
                    <v-icon v-if="canEdit && !editing" x-small color="grey" class="ml-1" style="opacity:0.35;">mdi-pencil</v-icon>
                </div>
                <v-text-field
                    v-else
                    v-model="titleDraft"
                    dense hide-details autofocus
                    class="doc-title-field"
                    @blur="saveTitle"
                    @keydown.enter="saveTitle"
                    @keydown.esc="editingTitle = false"
                ></v-text-field>

                <v-spacer></v-spacer>

                <!-- Search (view mode only) -->
                <template v-if="!editing">
                    <div class="doc-search-wrap">
                        <v-text-field
                            v-model="searchTerm"
                            placeholder="Search…"
                            dense hide-details clearable
                            prepend-inner-icon="mdi-magnify"
                            class="doc-search-field"
                            @keydown.enter.native="nextMatch"
                        ></v-text-field>
                        <template v-if="searchTerm && searchTerm.trim()">
                            <span v-if="matchCount > 0" class="doc-match-badge">
                                {{ currentMatch + 1 }}/{{ matchCount }}
                            </span>
                            <span v-else class="doc-match-badge doc-match-none">No matches</span>
                            <v-btn icon x-small :disabled="matchCount < 2" @click="prevMatch" v-tooltip="'Previous (Shift+Enter)'">
                                <v-icon x-small>mdi-chevron-up</v-icon>
                            </v-btn>
                            <v-btn icon x-small :disabled="matchCount < 2" @click="nextMatch" v-tooltip="'Next (Enter)'">
                                <v-icon x-small>mdi-chevron-down</v-icon>
                            </v-btn>
                        </template>
                    </div>
                </template>

                <!-- Action buttons -->
                <div class="doc-toolbar-actions">
                    <!-- Status badge (KB docs) — Draft is admin-only until published -->
                    <v-menu v-if="doc && canEdit && !editing && modelType === 'system'" offset-y>
                        <template v-slot:activator="{ on }">
                            <v-chip x-small label class="mr-1" v-on="on" style="height:18px;font-size:10px;font-weight:600;letter-spacing:.4px;cursor:pointer;"
                                :color="doc.status === 'draft' ? 'orange lighten-4' : 'green lighten-5'"
                                :text-color="doc.status === 'draft' ? 'orange darken-3' : 'green darken-2'">
                                {{ doc.status === 'draft' ? 'Draft' : 'Published' }}
                                <v-icon x-small class="ml-1" :color="doc.status === 'draft' ? 'orange darken-3' : 'green darken-2'">mdi-chevron-down</v-icon>
                            </v-chip>
                        </template>
                        <v-list dense>
                            <v-list-item :disabled="doc.status === 'published'" @click="changeStatus('published')">
                                <v-list-item-icon class="mr-2 my-auto"><v-icon small color="green darken-1">mdi-earth</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title style="font-size:13px;">Published</v-list-item-title>
                                    <v-list-item-subtitle class="caption">Visible to all users</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-icon v-if="doc.status === 'published'" x-small color="success" class="ml-2">mdi-check</v-icon>
                            </v-list-item>
                            <v-list-item :disabled="doc.status === 'draft'" @click="changeStatus('draft')">
                                <v-list-item-icon class="mr-2 my-auto"><v-icon small color="orange darken-1">mdi-pencil-outline</v-icon></v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title style="font-size:13px;">Draft</v-list-item-title>
                                    <v-list-item-subtitle class="caption">Admins only</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-icon v-if="doc.status === 'draft'" x-small color="success" class="ml-2">mdi-check</v-icon>
                            </v-list-item>
                        </v-list>
                    </v-menu>

                    <!-- Format badge — clickable when canEdit -->
                    <v-menu v-if="doc && canEdit && !editing" offset-y>
                        <template v-slot:activator="{ on }">
                            <v-chip x-small label class="mr-1" v-on="on" style="height:18px;font-size:10px;font-weight:600;letter-spacing:.4px;cursor:pointer;"
                                :color="doc.format === 'markdown' ? 'deep-orange lighten-5' : 'blue-grey lighten-5'"
                                :text-color="doc.format === 'markdown' ? 'deep-orange darken-2' : 'blue-grey darken-1'">
                                {{ doc.format === 'markdown' ? 'MD' : 'Rich Text' }}
                                <v-icon x-small class="ml-1" :color="doc.format === 'markdown' ? 'deep-orange darken-2' : 'blue-grey darken-1'">mdi-chevron-down</v-icon>
                            </v-chip>
                        </template>
                        <v-list dense>
                            <v-list-item :disabled="doc.format === 'html'" @click="changeFormat('html')">
                                <v-list-item-icon class="mr-2 my-auto"><v-icon small color="blue darken-1">mdi-format-text</v-icon></v-list-item-icon>
                                <v-list-item-content><v-list-item-title style="font-size:13px;">Rich Text</v-list-item-title></v-list-item-content>
                                <v-icon v-if="doc.format === 'html'" x-small color="success" class="ml-2">mdi-check</v-icon>
                            </v-list-item>
                            <v-list-item :disabled="doc.format === 'markdown'" @click="changeFormat('markdown')">
                                <v-list-item-icon class="mr-2 my-auto"><v-icon small color="deep-orange darken-1">mdi-language-markdown-outline</v-icon></v-list-item-icon>
                                <v-list-item-content><v-list-item-title style="font-size:13px;">Markdown</v-list-item-title></v-list-item-content>
                                <v-icon v-if="doc.format === 'markdown'" x-small color="success" class="ml-2">mdi-check</v-icon>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-chip v-else-if="doc" x-small label class="mr-1"
                        :color="doc.format === 'markdown' ? 'deep-orange lighten-5' : 'blue-grey lighten-5'"
                        :text-color="doc.format === 'markdown' ? 'deep-orange darken-2' : 'blue-grey darken-1'"
                        style="height:18px;font-size:10px;font-weight:600;letter-spacing:.4px;">
                        {{ doc.format === 'markdown' ? 'MD' : 'Rich Text' }}
                    </v-chip>
                    <template v-if="!editing">
                        <v-btn v-if="canEdit" icon small v-tooltip="'Edit'" @click="startEdit">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small v-tooltip="'Version history'" @click="toggleHistory">
                            <v-icon small :color="historyOpen ? 'primary' : ''">mdi-history</v-icon>
                        </v-btn>
                        <v-btn v-if="canEdit" icon small v-tooltip="'Delete document'" @click="confirmDelete">
                            <v-icon small color="red lighten-2">mdi-delete-outline</v-icon>
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn small text color="grey" @click="cancelEdit">Cancel</v-btn>
                        <v-btn small outlined color="blue-grey" :loading="saving" @click="saveUpdate" class="ml-1">
                            Update
                        </v-btn>
                        <v-btn small color="primary" :loading="savingVersion" @click="saveVersion" class="ml-1">
                            <v-icon small class="mr-1">mdi-bookmark-check-outline</v-icon>Save Version
                        </v-btn>
                    </template>
                    <v-btn icon small class="ml-2" @click="dialogOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>

            <!-- Change summary bar (edit mode) -->
            <v-expand-transition>
                <div v-if="editing" class="doc-summary-bar">
                    <v-text-field
                        v-model="summary"
                        label="Change summary (optional)"
                        placeholder="e.g. Added deployment steps"
                        dense hide-details outlined
                        class="caption"
                        style="max-width:480px;"
                    ></v-text-field>
                </div>
            </v-expand-transition>

            <v-divider></v-divider>

            <!-- ══════════════════════════════════════
                 BODY  (TOC sidebar + content)
            ══════════════════════════════════════════ -->
            <div class="doc-dialog-body">

                <!-- TOC sidebar -->
                <div v-if="toc.length && !$vuetify.breakpoint.smAndDown" class="doc-toc">
                    <div class="doc-toc-header">Contents</div>
                    <div
                        v-for="item in toc"
                        :key="item.id"
                        class="doc-toc-item"
                        :class="[`doc-toc-h${item.level}`, { 'doc-toc-active': activeHeading === item.id }]"
                        @click="scrollTo(item.id)"
                    >{{ item.text }}</div>
                </div>

                <!-- Content / Editor -->
                <div class="doc-dialog-content" ref="contentArea">

                    <!-- Loading -->
                    <div v-if="loading" class="d-flex justify-center align-center" style="height:200px;">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                    </div>

                    <!-- View mode -->
                    <div v-else-if="!editing" class="doc-view-html" v-html="displayHtml"></div>

                    <!-- Edit mode — Rich Text -->
                    <doc-editor v-else-if="doc.format !== 'markdown'" :value="editContent" @input="editContent = $event"></doc-editor>

                    <!-- Edit mode — Markdown -->
                    <div v-else class="md-edit-wrap">
                        <!-- Write / Preview tabs -->
                        <div class="md-tabs">
                            <button :class="['md-tab', { 'md-tab--active': mdTab === 'write' }]" @click="mdTab = 'write'">Write</button>
                            <button :class="['md-tab', { 'md-tab--active': mdTab === 'preview' }]" @click="mdTab = 'preview'">Preview</button>
                        </div>

                        <!-- Markdown toolbar (write mode only) -->
                        <div v-show="mdTab === 'write'" class="md-toolbar">
                            <div class="md-toolbar-group">
                                <button class="md-toolbar-btn" v-tooltip="'Bold'" @mousedown.prevent="mdWrap('**','**','bold text')"><strong style="font-size:13px;">B</strong></button>
                                <button class="md-toolbar-btn" v-tooltip="'Italic'" @mousedown.prevent="mdWrap('*','*','italic text')"><em style="font-size:13px;">I</em></button>
                                <button class="md-toolbar-btn" v-tooltip="'Strikethrough'" @mousedown.prevent="mdWrap('~~','~~','text')"><s style="font-size:13px;">S</s></button>
                            </div>
                            <div class="md-toolbar-sep"></div>
                            <div class="md-toolbar-group">
                                <button class="md-toolbar-btn" v-tooltip="'Heading 1'" @mousedown.prevent="mdLine('# ')"><span style="font-size:11px;font-weight:700;">H1</span></button>
                                <button class="md-toolbar-btn" v-tooltip="'Heading 2'" @mousedown.prevent="mdLine('## ')"><span style="font-size:11px;font-weight:700;">H2</span></button>
                                <button class="md-toolbar-btn" v-tooltip="'Heading 3'" @mousedown.prevent="mdLine('### ')"><span style="font-size:11px;font-weight:700;">H3</span></button>
                            </div>
                            <div class="md-toolbar-sep"></div>
                            <div class="md-toolbar-group">
                                <button class="md-toolbar-btn" v-tooltip="'Inline code'" @mousedown.prevent="mdWrap('`','`','code')"><v-icon x-small>mdi-code-tags</v-icon></button>
                                <button class="md-toolbar-btn" v-tooltip="'Code block'" @mousedown.prevent="mdCodeBlock"><v-icon x-small>mdi-code-braces</v-icon></button>
                            </div>
                            <div class="md-toolbar-sep"></div>
                            <div class="md-toolbar-group">
                                <button class="md-toolbar-btn" v-tooltip="'Bullet list'" @mousedown.prevent="mdLine('- ')"><v-icon x-small>mdi-format-list-bulleted</v-icon></button>
                                <button class="md-toolbar-btn" v-tooltip="'Ordered list'" @mousedown.prevent="mdLine('1. ')"><v-icon x-small>mdi-format-list-numbered</v-icon></button>
                                <button class="md-toolbar-btn" v-tooltip="'Blockquote'" @mousedown.prevent="mdLine('> ')"><v-icon x-small>mdi-format-quote-close</v-icon></button>
                            </div>
                            <div class="md-toolbar-sep"></div>
                            <div class="md-toolbar-group">
                                <button class="md-toolbar-btn" v-tooltip="'Link'" @mousedown.prevent="mdLink"><v-icon x-small>mdi-link-variant</v-icon></button>
                                <button class="md-toolbar-btn" v-tooltip="'Table'" @mousedown.prevent="mdTable"><v-icon x-small>mdi-table-plus</v-icon></button>
                                <button class="md-toolbar-btn" v-tooltip="'Horizontal rule'" @mousedown.prevent="mdHr"><v-icon x-small>mdi-minus</v-icon></button>
                            </div>
                        </div>

                        <textarea
                            v-show="mdTab === 'write'"
                            ref="mdTextarea"
                            v-model="editContent"
                            class="md-textarea"
                            placeholder="Write markdown here…"
                            spellcheck="false"
                        ></textarea>
                        <div
                            v-show="mdTab === 'preview'"
                            class="doc-view-html md-preview"
                            v-html="mdPreviewHtml"
                        ></div>
                    </div>

                </div>

                <!-- ── Version history right panel ── -->
                <div v-if="historyOpen" class="doc-history-panel">
                    <div class="doc-history-panel-head">
                        <v-icon small class="mr-1" color="grey darken-1">mdi-history</v-icon>
                        <span class="doc-history-label">Version History</span>
                        <v-spacer></v-spacer>
                        <v-btn icon x-small @click="historyOpen = false">
                            <v-icon x-small>mdi-close</v-icon>
                        </v-btn>
                    </div>

                    <div v-if="versionsLoading" class="d-flex justify-center py-4">
                        <v-progress-circular indeterminate size="20" color="grey"></v-progress-circular>
                    </div>
                    <div v-else-if="!versions.length" class="caption grey--text pa-3">No versions saved yet.</div>
                    <div v-else class="doc-version-list">
                        <div v-for="v in versions" :key="v.id" class="doc-version-row">
                            <div class="doc-version-row-top">
                                <v-chip x-small color="blue lighten-5" class="font-weight-bold" style="min-width:34px;justify-content:center;">
                                    v{{ v.version }}
                                </v-chip>
                                <div class="doc-version-actions">
                                    <v-btn icon x-small v-tooltip="'Diff vs current'" @click="openDiff(v)">
                                        <v-icon x-small color="purple">mdi-compare-horizontal</v-icon>
                                    </v-btn>
                                    <v-btn icon x-small v-tooltip="'Preview'" @click="openPreview(v)">
                                        <v-icon x-small>mdi-eye-outline</v-icon>
                                    </v-btn>
                                    <v-btn icon x-small v-tooltip="'Restore'" :loading="v._restoring" @click="restore(v)">
                                        <v-icon x-small color="orange darken-1">mdi-restore</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <div class="doc-version-summary">{{ v.summary || '(no summary)' }}</div>
                            <div class="doc-version-meta">{{ v.created_by_label || v.created_by }} · {{ formatDate(v.created_at) }}</div>
                        </div>
                    </div>
                </div>

            </div>
        </v-card>

        <!-- ══════════════════════════════════════
             DIFF DIALOG
        ══════════════════════════════════════════ -->
        <v-dialog v-model="diffOpen" max-width="860">
            <v-card v-if="diffVersion">
                <v-card-title class="py-2 px-4 d-flex align-center">
                    <v-icon small class="mr-2" color="purple">mdi-compare-horizontal</v-icon>
                    <v-chip small color="blue lighten-4" class="mr-2 font-weight-bold">v{{ diffVersion.version }}</v-chip>
                    <span class="body-2 grey--text mr-1">vs</span>
                    <span class="body-2">Current</span>
                    <v-spacer></v-spacer>
                    <div class="d-flex align-center caption grey--text mr-3" style="gap:12px;">
                        <span><span class="diff-legend diff-legend--add"></span> Added</span>
                        <span><span class="diff-legend diff-legend--remove"></span> Removed</span>
                    </div>
                    <v-btn icon small @click="diffOpen = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-4" style="max-height:68vh;overflow-y:auto;">
                    <div class="doc-diff-view" v-html="diffHtml"></div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small @click="diffOpen = false">Close</v-btn>
                    <v-btn color="orange" small :loading="diffVersion._restoring" @click="restore(diffVersion)">
                        <v-icon small class="mr-1">mdi-restore</v-icon>Restore v{{ diffVersion.version }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ══════════════════════════════════════
             VERSION PREVIEW DIALOG
        ══════════════════════════════════════════ -->
        <v-dialog v-model="previewOpen" max-width="820">
            <v-card v-if="previewVersion">
                <v-card-title class="py-2 px-4 d-flex align-center">
                    <v-chip small color="blue lighten-4" class="mr-2 font-weight-bold">v{{ previewVersion.version }}</v-chip>
                    <span class="body-2">{{ doc && doc.title }}</span>
                    <span class="caption grey--text ml-2">{{ formatDate(previewVersion.created_at) }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="previewOpen = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-3" style="max-height:62vh;overflow-y:auto;">
                    <div v-if="doc && doc.format === 'markdown'" class="doc-view-html pa-2" v-html="previewRenderedHtml"></div>
                    <doc-editor v-else :value="previewVersion.content" :disabled="true"></doc-editor>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text small @click="previewOpen = false">Close</v-btn>
                    <v-btn color="orange" small :loading="previewVersion._restoring" @click="restore(previewVersion)">
                        <v-icon small class="mr-1">mdi-restore</v-icon>Restore this version
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import swal from 'sweetalert'
import DocEditor from './DocEditor.vue'

import hljs from 'highlight.js/lib/core'
import javascript  from 'highlight.js/lib/languages/javascript'
import typescript  from 'highlight.js/lib/languages/typescript'
import python      from 'highlight.js/lib/languages/python'
import php         from 'highlight.js/lib/languages/php'
import bash        from 'highlight.js/lib/languages/bash'
import sql         from 'highlight.js/lib/languages/sql'
import json        from 'highlight.js/lib/languages/json'
import css         from 'highlight.js/lib/languages/css'
import xml         from 'highlight.js/lib/languages/xml'
import java        from 'highlight.js/lib/languages/java'
import csharp      from 'highlight.js/lib/languages/csharp'
import cpp         from 'highlight.js/lib/languages/cpp'
import go          from 'highlight.js/lib/languages/go'
import kotlin      from 'highlight.js/lib/languages/kotlin'
import dart        from 'highlight.js/lib/languages/dart'
import yaml        from 'highlight.js/lib/languages/yaml'
import markdown    from 'highlight.js/lib/languages/markdown'
import plaintext   from 'highlight.js/lib/languages/plaintext'

hljs.registerLanguage('javascript',  javascript)
hljs.registerLanguage('typescript',  typescript)
hljs.registerLanguage('python',      python)
hljs.registerLanguage('php',         php)
hljs.registerLanguage('bash',        bash)
hljs.registerLanguage('shell',       bash)
hljs.registerLanguage('sql',         sql)
hljs.registerLanguage('json',        json)
hljs.registerLanguage('css',         css)
hljs.registerLanguage('html',        xml)
hljs.registerLanguage('xml',         xml)
hljs.registerLanguage('java',        java)
hljs.registerLanguage('csharp',      csharp)
hljs.registerLanguage('cpp',         cpp)
hljs.registerLanguage('go',          go)
hljs.registerLanguage('kotlin',      kotlin)
hljs.registerLanguage('dart',        dart)
hljs.registerLanguage('yaml',        yaml)
hljs.registerLanguage('markdown',    markdown)
hljs.registerLanguage('plaintext',   plaintext)

// ── Markdown renderer ─────────────────────────────────
import MarkdownIt from 'markdown-it'

const _escapeHtml = str => str
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const md = new MarkdownIt({
    html:        true,   // allow raw HTML in .md files (e.g. <div>, <table align="center">)
    linkify:     true,
    typographer: true,
    highlight(str, lang) {
        if (lang && hljs.getLanguage(lang)) {
            try {
                return '<pre><code class="hljs language-' + lang + '">' +
                    hljs.highlight(str, { language: lang }).value +
                    '</code></pre>'
            } catch (_) { /* fall through */ }
        }
        return '<pre><code>' + _escapeHtml(str) + '</code></pre>'
    },
})

export default {
    name: 'DocDialog',
    components: { DocEditor },

    props: {
        canEdit: { type: Boolean, default: true },
    },

    data: () => ({
        dialogOpen: false,
        loading:    false,

        // context
        modelType: '',
        modelId:   null,
        doc:       null,

        // content processing
        processedHtml: '',
        toc:           [],
        activeHeading: null,

        // search
        searchTerm:   '',
        currentMatch: 0,

        // editing
        editContent:   '',
        editing:       false,
        saving:        false,
        savingVersion: false,
        summary:       '',
        mdTab:         'write', // 'write' | 'preview' (markdown edit mode only)

        // diff
        diffOpen:    false,
        diffVersion: null,
        diffHtml:    '',

        // title editing
        editingTitle: false,
        titleDraft:   '',

        // version history
        historyOpen:     false,
        versions:        [],
        versionsLoading: false,

        // version preview
        previewOpen:    false,
        previewVersion: null,
    }),

    watch: {
        searchTerm(val) {
            this.currentMatch = 0
            if (val && val.trim()) {
                this.$nextTick(() => this._scrollToMatch(0))
            }
        },

        dialogOpen(val) {
            if (val) {
                this.$nextTick(() => {
                    const el = this.$refs.contentArea
                    if (el) el.addEventListener('scroll', this._onContentScroll, { passive: true })
                })
            } else {
                const el = this.$refs.contentArea
                if (el) el.removeEventListener('scroll', this._onContentScroll)
            }
        },
    },

    beforeDestroy() {
        const el = this.$refs.contentArea
        if (el) el.removeEventListener('scroll', this._onContentScroll)
    },

    computed: {
        displayHtml() {
            let html = this.processedHtml
            const term = this.searchTerm && this.searchTerm.trim()
            if (term) html = this.highlightSearch(html, term)
            return html
        },

        matchCount() {
            const term = this.searchTerm && this.searchTerm.trim()
            if (!term || !this.doc) return 0
            const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            // For HTML docs strip tags; markdown is already plain-ish text
            const plain = (this.doc.content || '').replace(/<[^>]+>/g, '')
            return (plain.match(new RegExp(escaped, 'gi')) || []).length
        },

        // Live preview for markdown edit mode
        mdPreviewHtml() {
            if (!this.doc || this.doc.format !== 'markdown') return ''
            return this._mdToHtml(this.editContent || '')
        },

        // Rendered content for version preview dialog (markdown docs)
        previewRenderedHtml() {
            if (!this.previewVersion) return ''
            return this._mdToHtml(this.previewVersion.content || '')
        },
    },

    methods: {
        // ── Public API ──────────────────────────────────────────
        async open(modelType, modelId, docSummary) {
            this.modelType     = modelType
            this.modelId       = modelId
            this.doc           = null
            this.processedHtml = ''
            this.toc           = []
            this.activeHeading = null
            this.searchTerm    = ''
            this.editing       = false
            this.editingTitle  = false
            this.historyOpen   = false
            this.versions      = []
            this.dialogOpen    = true
            this.loading       = true

            try {
                const resp = await this.$axios.get(
                    `/api/docs/${modelType}/${modelId}/${docSummary.id}`
                )
                this.doc = resp.data.data
                this.editContent = this.doc.content || ''
                this._processContent()
            } catch (err) {
                console.error('DocDialog open error:', err)
            } finally {
                this.loading = false
            }
        },

        _processContent() {
            const sourceHtml = this.doc.format === 'markdown'
                ? this._mdToHtml(this.doc.content || '')
                : (this.doc.content || '')
            const { html, toc } = this._buildToc(sourceHtml)
            this.processedHtml = html
            this.toc           = toc
        },

        _mdToHtml(content) {
            return md.render(content || '')
        },

        // ── TOC + syntax highlighting ────────────────────────────
        _buildToc(html) {
            if (!html) return { html: '', toc: [] }
            const dom = new DOMParser().parseFromString(html, 'text/html')

            // Apply syntax highlighting to every <pre><code> block
            // Skip blocks already highlighted by markdown-it (have class 'hljs')
            dom.querySelectorAll('pre code').forEach(block => {
                if (block.classList.contains('hljs')) return
                const classes  = Array.from(block.classList)
                const langCls  = classes.find(c => c.startsWith('language-'))
                const lang     = langCls ? langCls.replace('language-', '') : null
                try {
                    const result = lang && hljs.getLanguage(lang)
                        ? hljs.highlight(block.textContent, { language: lang })
                        : hljs.highlightAuto(block.textContent)
                    block.innerHTML = result.value
                    block.classList.add('hljs')
                } catch (_) { /* leave unhighlighted on error */ }
            })

            // Build TOC from headings
            const headings = dom.querySelectorAll('h1, h2, h3')
            const toc      = []
            headings.forEach((h, i) => {
                const id = `doc-h-${i}`
                h.setAttribute('id', id)
                toc.push({ id, text: h.textContent.trim(), level: parseInt(h.tagName[1]) })
            })
            return { html: dom.body.innerHTML, toc }
        },

        scrollTo(id) {
            if (!this.$refs.contentArea || this.editing) return
            const el = this.$refs.contentArea.querySelector(`#${id}`)
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                this.activeHeading = id
            }
        },

        _onContentScroll() {
            if (!this.$refs.contentArea || !this.toc.length) return
            const container  = this.$refs.contentArea
            const threshold  = container.getBoundingClientRect().top + 60
            let   activeId   = null
            for (const item of this.toc) {
                const el = container.querySelector(`#${item.id}`)
                if (!el) continue
                if (el.getBoundingClientRect().top <= threshold) {
                    activeId = item.id
                }
            }
            if (activeId !== null) this.activeHeading = activeId
        },

        // ── Search ──────────────────────────────────────────────
        highlightSearch(html, term) {
            const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const rx      = new RegExp(`(${escaped})`, 'gi')
            // Replace only in text nodes (not inside HTML tags)
            return html.replace(/(<[^>]+>)|([^<]+)/g, (match, tag, text) => {
                if (tag) return tag
                return text.replace(rx, '<mark class="doc-highlight">$1</mark>')
            })
        },

        nextMatch() {
            if (!this.matchCount) return
            this.currentMatch = (this.currentMatch + 1) % this.matchCount
            this._scrollToMatch(this.currentMatch)
        },

        prevMatch() {
            if (!this.matchCount) return
            this.currentMatch = (this.currentMatch - 1 + this.matchCount) % this.matchCount
            this._scrollToMatch(this.currentMatch)
        },

        _scrollToMatch(index) {
            const area = this.$refs.contentArea
            if (!area) return
            const marks = area.querySelectorAll('mark.doc-highlight')
            marks.forEach(m => m.classList.remove('doc-highlight--active'))
            if (!marks.length) return
            const target = marks[index]
            if (!target) return
            target.classList.add('doc-highlight--active')
            target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },

        // ── Title ───────────────────────────────────────────────
        startEditTitle() {
            this.titleDraft  = this.doc.title
            this.editingTitle = true
        },

        async saveTitle() {
            this.editingTitle = false
            if (this.titleDraft === this.doc.title) return
            try {
                const resp = await this.$axios.put(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}`,
                    { title: this.titleDraft }
                )
                this.doc = resp.data.data
                this.$emit('refresh')
            } catch (err) {
                console.error('DocDialog saveTitle error:', err)
            }
        },

        // ── Edit ────────────────────────────────────────────────
        startEdit() {
            this.editContent = this.doc.content || ''
            this.summary     = ''
            this.mdTab       = 'write'
            this.editing     = true
        },

        cancelEdit() {
            this.editContent = this.doc.content || ''
            this.editing     = false
        },

        async changeStatus(newStatus) {
            if (!this.doc || this.doc.status === newStatus) return
            try {
                const resp = await this.$axios.put(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}`,
                    { status: newStatus, no_version: true }
                )
                this.doc = resp.data.data
                this.$emit('refresh')
            } catch (err) {
                console.error('DocDialog changeStatus error:', err)
            }
        },

        async changeFormat(newFormat) {
            if (!this.doc || this.doc.format === newFormat) return
            try {
                const resp = await this.$axios.put(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}`,
                    { format: newFormat, no_version: true }
                )
                this.doc = resp.data.data
                this._processContent()
                this.$emit('refresh')
            } catch (err) {
                console.error('DocDialog changeFormat error:', err)
            }
        },

        // ── Markdown toolbar helpers ─────────────────────────────
        // Wrap selected text (or placeholder) with prefix + suffix
        mdWrap(prefix, suffix, placeholder) {
            const ta = this.$refs.mdTextarea
            if (!ta) return
            const start  = ta.selectionStart
            const end    = ta.selectionEnd
            const sel    = this.editContent.substring(start, end) || placeholder
            const before = this.editContent.substring(0, start)
            const after  = this.editContent.substring(end)
            this.editContent = before + prefix + sel + suffix + after
            this.$nextTick(() => {
                ta.focus()
                ta.setSelectionRange(start + prefix.length, start + prefix.length + sel.length)
            })
        },

        // Toggle a line prefix (headings, lists, blockquote)
        mdLine(prefix) {
            const ta = this.$refs.mdTextarea
            if (!ta) return
            const pos       = ta.selectionStart
            const text      = this.editContent
            const lineStart = text.lastIndexOf('\n', pos - 1) + 1
            const lineEnd   = text.indexOf('\n', pos)
            const end       = lineEnd === -1 ? text.length : lineEnd
            const line      = text.substring(lineStart, end)
            const newLine   = line.startsWith(prefix) ? line.slice(prefix.length) : prefix + line
            this.editContent = text.substring(0, lineStart) + newLine + text.substring(end)
            this.$nextTick(() => { ta.focus() })
        },

        mdCodeBlock() {
            const ta = this.$refs.mdTextarea
            if (!ta) return
            const start  = ta.selectionStart
            const end    = ta.selectionEnd
            const sel    = this.editContent.substring(start, end) || 'code'
            const before = this.editContent.substring(0, start)
            const after  = this.editContent.substring(end)
            const pre    = before.length && !before.endsWith('\n') ? '\n' : ''
            const post   = after.length  && !after.startsWith('\n') ? '\n' : ''
            const insert = pre + '```\n' + sel + '\n```' + post
            this.editContent = before + insert + after
            this.$nextTick(() => {
                ta.focus()
                const codeStart = start + pre.length + 4
                ta.setSelectionRange(codeStart, codeStart + sel.length)
            })
        },

        mdLink() {
            const ta = this.$refs.mdTextarea
            if (!ta) return
            const start  = ta.selectionStart
            const end    = ta.selectionEnd
            const sel    = this.editContent.substring(start, end) || 'link text'
            const before = this.editContent.substring(0, start)
            const after  = this.editContent.substring(end)
            this.editContent = before + '[' + sel + '](url)' + after
            this.$nextTick(() => {
                ta.focus()
                const urlStart = start + sel.length + 3
                ta.setSelectionRange(urlStart, urlStart + 3)
            })
        },

        mdTable() {
            const ta = this.$refs.mdTextarea
            if (!ta) return
            const pos    = ta.selectionStart
            const before = this.editContent.substring(0, pos)
            const after  = this.editContent.substring(pos)
            const pre    = before.length && !before.endsWith('\n') ? '\n' : ''
            const table  = pre + '| Column 1 | Column 2 | Column 3 |\n|----------|----------|----------|\n| Cell     | Cell     | Cell     |\n'
            this.editContent = before + table + after
            this.$nextTick(() => { ta.focus() })
        },

        mdHr() {
            const ta = this.$refs.mdTextarea
            if (!ta) return
            const pos    = ta.selectionStart
            const before = this.editContent.substring(0, pos)
            const after  = this.editContent.substring(pos)
            const pre    = before.length && !before.endsWith('\n') ? '\n' : ''
            this.editContent = before + pre + '\n---\n\n' + after
            this.$nextTick(() => { ta.focus() })
        },

        // Save content only — no version snapshot
        async saveUpdate() {
            this.saving = true
            try {
                const resp = await this.$axios.put(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}`,
                    { content: this.editContent, no_version: true }
                )
                this.doc = resp.data.data
                this._processContent()
                this.editing = false
                this.$emit('refresh')
            } catch (err) {
                console.error('DocDialog saveUpdate error:', err)
            } finally {
                this.saving = false
            }
        },

        // Save content + create version snapshot
        async saveVersion() {
            this.savingVersion = true
            try {
                const resp = await this.$axios.put(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}`,
                    { content: this.editContent, summary: this.summary || null }
                )
                this.doc = resp.data.data
                this._processContent()
                this.editing = false
                this.summary = ''
                this.$emit('refresh')
                if (this.historyOpen) await this.loadVersions()
            } catch (err) {
                console.error('DocDialog saveVersion error:', err)
            } finally {
                this.savingVersion = false
            }
        },

        // ── Delete ──────────────────────────────────────────────
        async confirmDelete() {
            const ok = await swal({
                title: 'Delete document?',
                text:  `"${this.doc.title}" and all its versions will be permanently removed.`,
                icon:  'warning',
                buttons:    true,
                dangerMode: true,
            })
            if (!ok) return
            try {
                await this.$axios.delete(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}`
                )
                this.$emit('refresh')
                this.dialogOpen = false
            } catch (err) {
                console.error('DocDialog delete error:', err)
            }
        },

        // ── Version history ─────────────────────────────────────
        async toggleHistory() {
            this.historyOpen = !this.historyOpen
            if (this.historyOpen) await this.loadVersions()
        },

        async loadVersions() {
            this.versionsLoading = true
            try {
                const resp = await this.$axios.get(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}/versions`
                )
                this.versions = (resp.data.data || []).map(v => ({ ...v, _restoring: false }))
            } catch (err) {
                console.error('DocDialog versions error:', err)
            } finally {
                this.versionsLoading = false
            }
        },

        async openPreview(v) {
            try {
                const resp = await this.$axios.get(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}/versions/${v.id}`
                )
                this.previewVersion = { ...resp.data.data, _restoring: false }
                this.previewOpen    = true
            } catch (err) {
                console.error('DocDialog preview error:', err)
            }
        },

        // ── Diff ─────────────────────────────────────────────────
        async openDiff(v) {
            try {
                const resp = await this.$axios.get(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}/versions/${v.id}`
                )
                const versionContent = resp.data.data.content || ''
                const currentContent = this.doc.content || ''
                this.diffHtml    = this._computeBlockDiff(versionContent, currentContent)
                this.diffVersion = v
                this.diffOpen    = true
            } catch (err) {
                console.error('DocDialog diff error:', err)
            }
        },

        _computeBlockDiff(oldContent, newContent) {
            // Convert markdown to HTML first so DOMParser can extract blocks
            const toHtml = c => this.doc && this.doc.format === 'markdown'
                ? this._mdToHtml(c)
                : c
            const oldHtml = toHtml(oldContent)
            const newHtml = toHtml(newContent)

            const extractBlocks = html => {
                if (!html) return []
                const dom = new DOMParser().parseFromString(html, 'text/html')
                const result = []
                dom.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, pre, blockquote').forEach(el => {
                    const text = el.textContent.trim()
                    if (text) result.push({ text, outerHtml: el.outerHTML })
                })
                // Fallback: top-level child nodes
                if (!result.length) {
                    Array.from(dom.body.childNodes).forEach(node => {
                        const text = (node.textContent || '').trim()
                        if (text) result.push({ text, outerHtml: node.outerHTML || `<p>${text}</p>` })
                    })
                }
                return result
            }

            const oldBlocks = extractBlocks(oldHtml)
            const newBlocks = extractBlocks(newHtml)
            const m = oldBlocks.length, n = newBlocks.length

            // Build LCS DP table
            const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
            for (let i = 1; i <= m; i++)
                for (let j = 1; j <= n; j++)
                    dp[i][j] = oldBlocks[i - 1].text === newBlocks[j - 1].text
                        ? dp[i - 1][j - 1] + 1
                        : Math.max(dp[i - 1][j], dp[i][j - 1])

            // Backtrack to produce edit ops
            const ops = []
            let i = m, j = n
            while (i > 0 || j > 0) {
                if (i > 0 && j > 0 && oldBlocks[i - 1].text === newBlocks[j - 1].text) {
                    ops.unshift({ type: 'equal', oldIdx: i - 1, newIdx: j - 1 })
                    i--; j--
                } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
                    ops.unshift({ type: 'added', newIdx: j - 1 })
                    j--
                } else {
                    ops.unshift({ type: 'removed', oldIdx: i - 1 })
                    i--
                }
            }

            // Render diff HTML
            let html = ''
            for (const op of ops) {
                if (op.type === 'equal') {
                    html += `<div class="diff-equal">${newBlocks[op.newIdx].outerHtml}</div>`
                } else if (op.type === 'added') {
                    html += `<div class="diff-added">${newBlocks[op.newIdx].outerHtml}</div>`
                } else {
                    html += `<div class="diff-removed">${oldBlocks[op.oldIdx].outerHtml}</div>`
                }
            }
            return html || '<p class="grey--text text-center caption pa-4">No textual differences detected.</p>'
        },

        async restore(v) {
            this.$set(v, '_restoring', true)
            try {
                const resp = await this.$axios.post(
                    `/api/docs/${this.modelType}/${this.modelId}/${this.doc.id}/versions/${v.id}/restore`
                )
                this.doc         = resp.data.data
                this.editContent = this.doc.content || ''
                this._processContent()
                this.editing     = false
                this.previewOpen = false
                this.$emit('refresh')
                await this.loadVersions()
            } catch (err) {
                console.error('DocDialog restore error:', err)
            } finally {
                this.$set(v, '_restoring', false)
            }
        },

        // ── Utils ────────────────────────────────────────────────
        formatDate(iso) {
            if (!iso) return ''
            const d = new Date(iso)
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                 + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        },
    },
}
</script>

<style>
/* content-class targets the outer overlay — keep unscoped */
.doc-dialog-container .v-dialog {
    border-radius: 8px;
    overflow: hidden;
}
</style>

<style scoped>
/* ── Card layout ─────────────────────────────────────── */
.doc-dialog-card {
    display: flex;
    flex-direction: column;
    height: 88vh;
    max-height: 88vh;
    overflow: hidden;
}

/* ── Toolbar ─────────────────────────────────────────── */
.doc-dialog-toolbar {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    gap: 8px;
    flex-shrink: 0;
    min-height: 50px;
}
.doc-dialog-title {
    font-size: 15px;
    font-weight: 600;
    color: #212121;
    cursor: default;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 380px;
}
.doc-dialog-title:hover { cursor: pointer; }
.doc-title-field {
    max-width: 320px;
    font-size: 14px;
}
.doc-search-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
}
.doc-search-field {
    min-width: 180px;
    max-width: 240px;
}
.doc-match-badge {
    font-size: 11px;
    color: #1976d2;
    white-space: nowrap;
    font-weight: 500;
}
.doc-match-none { color: #e57373; }
.doc-toolbar-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

/* ── Summary bar ─────────────────────────────────────── */
.doc-summary-bar {
    padding: 8px 16px;
    background: #fafafa;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
}

/* ── Body ────────────────────────────────────────────── */
.doc-dialog-body {
    display: flex;
    flex: 1;
    overflow: hidden;
    min-height: 0;
}

/* ── TOC sidebar ─────────────────────────────────────── */
.doc-toc {
    width: 210px;
    flex-shrink: 0;
    overflow-y: auto;
    border-right: 1px solid #eeeeee;
    padding: 14px 10px;
    background: #fafafa;
}
.doc-toc-header {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #9e9e9e;
    margin-bottom: 8px;
    padding-left: 6px;
}
.doc-toc-item {
    font-size: 12px;
    color: #424242;
    cursor: pointer;
    padding: 3px 6px;
    border-radius: 4px;
    margin-bottom: 1px;
    line-height: 1.4;
    transition: background 0.1s, color 0.1s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.doc-toc-item:hover   { background: #eeeeee; color: #1976d2; }
.doc-toc-active       { background: #e3f2fd !important; color: #1976d2 !important; }
.doc-toc-h1           { font-weight: 600; }
.doc-toc-h2           { padding-left: 16px; }
.doc-toc-h3           { padding-left: 28px; font-size: 11px; color: #757575; }

/* ── Content area ────────────────────────────────────── */
.doc-dialog-content {
    flex: 1;
    overflow-y: auto;
    padding: 20px 28px 32px;
    min-width: 0;
}

/* ── Rendered HTML prose ─────────────────────────────── */
.doc-view-html {
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 1.75;
    color: #212121;
}
.doc-view-html :deep(h1) { font-size: 1.7em;  font-weight: 700; margin: 0.7em 0 0.35em; scroll-margin-top: 12px; }
.doc-view-html :deep(h2) { font-size: 1.35em; font-weight: 700; margin: 0.6em 0 0.3em;  scroll-margin-top: 12px; }
.doc-view-html :deep(h3) { font-size: 1.1em;  font-weight: 700; margin: 0.5em 0 0.25em; scroll-margin-top: 12px; }
.doc-view-html :deep(p)  { margin: 0 0 0.75em; }
.doc-view-html :deep(ul),
.doc-view-html :deep(ol) { padding-left: 1.6em; margin: 0.5em 0 0.75em; }
.doc-view-html :deep(li) { margin-bottom: 0.25em; }
.doc-view-html :deep(li > p) { margin-bottom: 0.25em; }
.doc-view-html :deep(blockquote) {
    border-left: 3px solid #bdbdbd;
    padding: 4px 14px;
    color: #757575;
    margin: 0.75em 0;
    background: #fafafa;
    border-radius: 0 4px 4px 0;
}
.doc-view-html :deep(code) {
    background: #f0f0f0;
    border-radius: 3px;
    padding: 1px 5px;
    font-family: 'Courier New', monospace;
    font-size: 0.87em;
}
.doc-view-html :deep(pre) {
    background: #1e1e2e;
    color: #cdd6f4;
    border-radius: 6px;
    padding: 14px 18px;
    margin: 0.8em 0;
    overflow-x: auto;
}
.doc-view-html :deep(pre code) {
    background: transparent;
    color: #cdd6f4;
    padding: 0;
    font-size: 13px;
    font-family: 'Courier New', monospace;
}

/* ── Syntax highlighting — Catppuccin Mocha palette ─── */
.doc-view-html :deep(.hljs)                         { color: #cdd6f4; background: transparent; }
.doc-view-html :deep(.hljs-keyword),
.doc-view-html :deep(.hljs-operator),
.doc-view-html :deep(.hljs-punctuation)             { color: #cba6f7; } /* mauve  — keywords   */
.doc-view-html :deep(.hljs-string),
.doc-view-html :deep(.hljs-regexp),
.doc-view-html :deep(.hljs-template-string),
.doc-view-html :deep(.hljs-template-variable)       { color: #a6e3a1; } /* green  — strings    */
.doc-view-html :deep(.hljs-number),
.doc-view-html :deep(.hljs-literal)                 { color: #fab387; } /* peach  — numbers    */
.doc-view-html :deep(.hljs-built_in),
.doc-view-html :deep(.hljs-type),
.doc-view-html :deep(.hljs-class),
.doc-view-html :deep(.hljs-title.class_)            { color: #89dceb; } /* sky    — types      */
.doc-view-html :deep(.hljs-function),
.doc-view-html :deep(.hljs-title),
.doc-view-html :deep(.hljs-title.function_)         { color: #89b4fa; } /* blue   — functions  */
.doc-view-html :deep(.hljs-variable),
.doc-view-html :deep(.hljs-params)                  { color: #cdd6f4; } /* text   — variables  */
.doc-view-html :deep(.hljs-comment),
.doc-view-html :deep(.hljs-quote)                   { color: #6c7086; font-style: italic; } /* overlay — comments */
.doc-view-html :deep(.hljs-attr),
.doc-view-html :deep(.hljs-attribute)               { color: #89b4fa; } /* blue   — attributes */
.doc-view-html :deep(.hljs-tag)                     { color: #f38ba8; } /* red    — HTML tags  */
.doc-view-html :deep(.hljs-name)                    { color: #cba6f7; } /* mauve  — tag names  */
.doc-view-html :deep(.hljs-selector-tag),
.doc-view-html :deep(.hljs-selector-class),
.doc-view-html :deep(.hljs-selector-id)             { color: #89dceb; } /* sky    — CSS selectors */
.doc-view-html :deep(.hljs-property)                { color: #89b4fa; } /* blue   — CSS props  */
.doc-view-html :deep(.hljs-meta),
.doc-view-html :deep(.hljs-meta .hljs-string)       { color: #f9e2af; } /* yellow — annotations */
.doc-view-html :deep(.hljs-doctag)                  { color: #6c7086; }
.doc-view-html :deep(.hljs-deletion)                { color: #f38ba8; background: rgba(243,139,168,.15); }
.doc-view-html :deep(.hljs-addition)                { color: #a6e3a1; background: rgba(166,227,161,.15); }
.doc-view-html :deep(.hljs-emphasis)                { font-style: italic; }
.doc-view-html :deep(.hljs-strong)                  { font-weight: bold; }
.doc-view-html :deep(img) {
    max-width: 100%;
    border-radius: 4px;
    margin: 8px 0;
}
.doc-view-html :deep(hr) {
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 1.2em 0;
}
.doc-view-html :deep(table) {
    border-collapse: collapse;
    width: 100%;
    margin: 1em 0;
    font-size: 13px;
    line-height: 1.5;
    overflow-x: auto;
    display: block;
}
.doc-view-html :deep(thead tr) {
    background: #f5f5f5;
}
.doc-view-html :deep(th) {
    background: #eeeeee;
    font-weight: 600;
    text-align: left;
    padding: 8px 12px;
    border: 1px solid #d0d0d0;
    white-space: nowrap;
    color: #424242;
}
.doc-view-html :deep(td) {
    padding: 7px 12px;
    border: 1px solid #e0e0e0;
    vertical-align: top;
    color: #212121;
}
.doc-view-html :deep(tbody tr:nth-child(even)) {
    background: #fafafa;
}
.doc-view-html :deep(tbody tr:hover) {
    background: #f0f7ff;
}

/* ── Search highlight ────────────────────────────────── */
.doc-view-html :deep(mark.doc-highlight),
:deep(mark.doc-highlight) {
    background: #fff176;
    border-radius: 2px;
    padding: 0 1px;
    color: inherit;
}
.doc-view-html :deep(mark.doc-highlight--active),
:deep(mark.doc-highlight--active) {
    background: #ff9800;
    color: #fff;
    outline: 2px solid #e65100;
    border-radius: 2px;
}

/* ── Version history right panel ─────────────────────── */
.doc-history-panel {
    width: 270px;
    min-width: 270px;
    flex-shrink: 0;
    border-left: 1px solid #e0e0e0;
    overflow-y: auto;
    background: #fafafa;
    display: flex;
    flex-direction: column;
}
.doc-history-panel-head {
    display: flex;
    align-items: center;
    padding: 10px 12px 8px;
    border-bottom: 1px solid #eeeeee;
    flex-shrink: 0;
}
.doc-history-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: #757575;
}
.doc-version-list {
    display: flex;
    flex-direction: column;
    padding: 6px 0;
}
.doc-version-row {
    padding: 8px 12px;
    border-bottom: 1px solid #f0f0f0;
    cursor: default;
}
.doc-version-row:hover { background: #f5f5f5; }
.doc-version-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
}
.doc-version-actions { display: flex; gap: 2px; }
.doc-version-summary {
    font-size: 12px;
    color: #424242;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.doc-version-meta { font-size: 10px; color: #9e9e9e; margin-top: 2px; }

/* ── Markdown edit area ──────────────────────────────── */
.md-edit-wrap {
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
}
.md-tabs {
    display: flex;
    border-bottom: 1px solid #e0e0e0;
    background: #f5f5f5;
    flex-shrink: 0;
}
.md-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 4px 6px;
    background: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;
}
.md-toolbar-group { display: flex; gap: 1px; }
.md-toolbar-sep {
    width: 1px;
    height: 20px;
    background: #ccc;
    margin: 0 3px;
}
.md-toolbar-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 26px;
    padding: 0 4px;
    border-radius: 3px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #424242;
    font-family: monospace;
    user-select: none;
    transition: background 0.1s;
}
.md-toolbar-btn:hover { background: #ddd; }
.md-toolbar-btn .v-icon { color: #424242 !important; }
.md-tab {
    padding: 5px 18px;
    font-size: 12px;
    font-weight: 500;
    color: #757575;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
    margin-bottom: -1px;
    transition: color 0.1s, border-color 0.1s;
}
.md-tab:hover { color: #424242; }
.md-tab--active {
    color: #1976d2;
    border-bottom-color: #1976d2;
    background: white;
}
.md-textarea {
    flex: 1;
    width: 100%;
    padding: 14px 16px;
    font-family: 'Cascadia Code', 'Fira Code', 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.65;
    border: none;
    outline: none;
    resize: none;
    background: #fafafa;
    color: #212121;
    min-height: 200px;
}
.md-preview {
    flex: 1;
    padding: 14px 16px;
    overflow-y: auto;
    background: white;
    min-height: 200px;
}

/* ── Diff view ────────────────────────────────────────── */
.doc-diff-view .diff-equal {
    padding: 2px 8px;
    color: #424242;
}
.doc-diff-view .diff-added {
    background: rgba(102, 187, 106, 0.15);
    border-left: 3px solid #66bb6a;
    padding: 3px 8px;
    margin: 2px 0;
}
.doc-diff-view .diff-removed {
    background: rgba(239, 83, 80, 0.1);
    border-left: 3px solid #ef5350;
    padding: 3px 8px;
    margin: 2px 0;
    text-decoration: line-through;
    opacity: 0.7;
}
/* Legend colour chips in the diff dialog title */
.diff-legend {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    vertical-align: middle;
    margin-right: 4px;
}
.diff-legend--add    { background: #66bb6a; }
.diff-legend--remove { background: #ef5350; }
</style>
